import ListFilter from '@components/list-filter'
import GridRow from '@components/grid-row'
import PageHeader from '@components/page-header'
import {Alert, Button, Card, Popconfirm, Space, Table, Tag} from 'antd'
import {Params, useDelete, useFetchGet} from 'helpers/api'
import {pageSizeOptions, paginationDefaults, widthLarge} from 'helpers/style'
import {isSysAdmin} from 'helpers/role'
import {useEffect, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {useTenant} from '@store/settings'

const Tokens = () => {
  const currentUser = useSelector(state => state.auth.user)
  const [tenant, setTenant] = useTenant()
  const [filter, setFilter] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(pageSizeOptions?.[0] || 10)
  const [tokens, setTokens] = useState(null)
  const [tokensMeta, setTokensMeta] = useState(null)
  const [error, setError] = useState(null)

  const fetchTokens = useFetchGet('auth/tokensview')
  const deleteToken = useDelete('auth/tokens')

  const formatDateTime = dateString => {
    if (!dateString) return null
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getRelativeTime = dateString => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    return formatDateTime(dateString)
  }

  const getTokens = () => {
    setError(null)
    const params = Params({filter, limit, page, order: 'DESC', sortBy: 'last_used', status: 'all'})
    if (isSysAdmin(currentUser) && tenant) params.append('tid', tenant.id)
    fetchTokens(params, response => {
      if (!response) {
        setTokens([])
        setTokensMeta(null)
        setError('Failed to load tokens')
        return
      }
      // Expecting { tokensview: [...], meta: {...} }
      setTokens(response.tokensview || response.tokens || [])
      setTokensMeta(response.meta || null)
    })
  }

  useEffect(() => {
    getTokens()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, page, limit, tenant, currentUser])

  const doRevokeToken = id => {
    deleteToken(id, () => {
      getTokens()
    })
  }

  const columns = useMemo(() => {
    const cols = [
      {
        title: 'Company',
        render: (_, record) => (
          <Button
            type="link"
            onClick={e => {
              e.stopPropagation()
              setTenant({id: record.tid, name: record.t_name, domain: record.t_domain})
            }}
          >
            {record.t_name}
          </Button>
        ),
        hidden: !isSysAdmin(currentUser) || !!tenant
      },
      {
        title: 'User',
        dataIndex: 'u_name',
        key: 'u_name'
      },
      {
        title: 'Token Name',
        dataIndex: 'name',
        key: 'name',
        render: name => name || <span style={{color: '#999'}}>—</span>
      },
      {
        title: 'Created',
        dataIndex: 'created',
        key: 'created',
        render: created => (
          <div>
            <div>{formatDateTime(created)}</div>
            <div style={{fontSize: '12px', color: '#999', marginTop: '2px'}}>{getRelativeTime(created)}</div>
          </div>
        )
      },
      {
        title: 'Last Activity',
        dataIndex: 'last_used',
        key: 'last_used',
        render: lastUsed => (
          <div>
            <div>{lastUsed ? formatDateTime(lastUsed) : <span style={{color: '#999'}}>Never used</span>}</div>
            {lastUsed && (
              <div style={{fontSize: '12px', color: '#999', marginTop: '2px'}}>{getRelativeTime(lastUsed)}</div>
            )}
          </div>
        )
      },
      {
        title: 'Status',
        dataIndex: 'revoked',
        key: 'status',
        render: revoked => (
          <div>
            <Tag color={revoked ? 'red' : 'green'} style={{marginBottom: '4px'}}>
              {revoked ? 'Revoked' : 'Active'}
            </Tag>
            {revoked && <div style={{fontSize: '11px', color: '#999'}}>{getRelativeTime(revoked)}</div>}
          </div>
        )
      },
      {
        title: 'Actions',
        key: 'action',
        align: 'right',
        render: (_, record) => (
          <Space size="middle">
            {!record.revoked ? (
              <Popconfirm
                title="Revoke Access Token"
                placement="topRight"
                onConfirm={() => doRevokeToken(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="link">Revoke</Button>
              </Popconfirm>
            ) : (
              <span style={{color: '#999'}}>—</span>
            )}
          </Space>
        )
      }
    ]
    return cols.filter(col => !col.hidden)
  }, [tenant, currentUser])

  const data = useMemo(() => {
    if (!tokens) return null
    return tokens
  }, [tokens])

  return (
    <>
      <GridRow layout={[24]}>
        <Card className={`w-${widthLarge}`}>
          <PageHeader
            ghost={false}
            onBack={() => window.history.back()}
            title="Access Tokens"
            subTitle=""
            backIcon={false}
            extra={[
              <ListFilter
                onFilter={value => {
                  setFilter(value)
                  setPage(1)
                }}
                filter={filter}
                tenant={tenant}
              />
            ]}
          />
          {error && <Alert message={error} type="error" showIcon style={{marginBottom: 16}} />}
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={!data}
            pagination={paginationDefaults(tokensMeta?.total, limit, setLimit, tokensMeta?.page || page, setPage)}
          />
        </Card>
      </GridRow>
    </>
  )
}

export default Tokens
